import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay, addHours, startOfDay, endOfDay } from 'date-fns';
import hu from 'date-fns/locale/hu';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
  IconButton,
  Stack,
  Tooltip,
  Snackbar,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  Event as EventIcon,
  Message as MessageIcon,
  Close,
  Phone,
  CloudUpload,
  Block,
  DeleteOutline,
  HelpOutline,
  Edit as EditIcon,
  AddCircleOutline,
} from '@mui/icons-material';
import DialogHeader from '../Common/DialogHeader';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import bookingService from '../../services/bookingService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL, { API_ENDPOINTS } from '../../config/api';
import ProviderCalendarHelp from './ProviderCalendarHelp';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

const locales = { 'hu': hu };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// DnD-képes naptár (a komponensen kívül, hogy ne jöjjön létre újra minden renderkor)
const DnDCalendar = withDragAndDrop(Calendar);

// ─── "Eltolt nap" trükk a Hét/Nap nézethez ─────────────────────────────────
// A react-big-calendar Hét/Nap nézete mindig valódi naptári napokhoz köti az
// oszlopokat (00:00-24:00), nincs beépített módja a nap-határ eltolására.
// Hogy a rács 7:00-tól induljon és éjfélen átlógva a következő nap kora
// reggeléig fusson, a MEGJELENÍTÉSHEZ minden eseményt (és a min/max ablakot)
// 7 órával "korábbra" toljuk — így ami a rácsban a 0. sor, az valójában 7:00,
// a 17. sor pedig a valós éjfél. A sor-címkéket és az esemény-időket eznek
// megfelelően vissza kell tolni, hogy a felhasználó a valós órát lássa;
// kattintás/húzás után pedig a kapott (eltolt) időt vissza kell alakítani
// valós idővé, mielőtt mentjük. Hónap és Lista nézetben nincs óránkénti rács,
// ott nincs értelme az eltolásnak — a valós dátumokkal dolgoznak változatlanul.
const DAY_START_HOUR = 7;
const shiftForDisplay = (date) => addHours(date, -DAY_START_HOUR);
const unshiftFromDisplay = (date) => addHours(date, DAY_START_HOUR);
const isTimeGridView = (view) => view === 'week' || view === 'day';

const DURATION_OPTIONS = [
  { value: 0.5, label: '30 perc' },
  { value: 1, label: '1 óra' },
  { value: 1.5, label: '1,5 óra' },
  { value: 2, label: '2 óra' },
  { value: 3, label: '3 óra' },
  { value: 4, label: '4 óra' },
  { value: 5, label: '5 óra' },
  { value: 6, label: '6 óra' },
  { value: 8, label: '8 óra' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'PENDING': return 'warning';
    case 'OFFER_SENT': return 'info';
    case 'OFFER_ACCEPTED': return 'primary';
    case 'OFFER_REJECTED': return 'error';
    case 'APPROVED': return 'success';
    case 'REJECTED': return 'error';
    case 'COMPLETED': return 'default';
    case 'CANCELLED': return 'default';
    default: return 'default';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'PENDING': return 'Ajánlatot kért';
    case 'OFFER_SENT': return 'Ajánlatot kapott';
    case 'OFFER_ACCEPTED': return 'Ajánlat elfogadva';
    case 'OFFER_REJECTED': return 'Ajánlat visszautasítva';
    case 'APPROVED': return 'Jóváhagyott foglalás';
    case 'REJECTED': return 'Elutasított foglalás';
    case 'COMPLETED': return 'Befejezett';
    case 'CANCELLED': return 'Lemondott';
    default: return status;
  }
};

const toDateStr = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toUtcDateStr = (date) => {
  const d = new Date(date);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

const applyTimeStr = (date, timeStr) => {
  if (!date || !timeStr) return date;
  const [h, m] = timeStr.split(':').map(Number);
  const result = new Date(date);
  result.setHours(h, m, 0, 0);
  return result;
};

// ─── DayEventsDialog ────────────────────────────────────────────────────────

const DayEventsDialog = ({ open, onClose, date, events, onSelectEvent, onAddEvent }) => {
  const theme = useTheme();
  if (!date) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ p: 0 }}>
        <DialogHeader
          icon={<EventIcon />}
          title={format(date, 'yyyy. MMMM d., EEEE', { locale: hu })}
          onClose={onClose}
        />
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {events.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2 }}>Nincs esemény ezen a napon.</Typography>
        ) : (
          <List disablePadding>
            {events.map((event, idx) => {
              const isBusy = event.type === 'BUSY';
              const statusColor = isBusy ? 'default' : getStatusColor(event.resource?.status);
              const statusText = isBusy
                ? (event.resource?.source === 'MANUAL' ? 'Személyes esemény' : 'Importált esemény')
                : getStatusText(event.resource?.status);
              const timeStr = isBusy
                ? (event.resource?.startTime ? event.resource.startTime : 'Egész nap')
                : event.resource?.eventTime;
              const dotColor = isBusy
                ? theme.palette.grey[500]
                : (theme.palette[statusColor]?.main || theme.palette.grey[500]);

              return (
                <ListItem key={event.id} disablePadding divider={idx < events.length - 1}>
                  <ListItemButton
                    onClick={() => { onClose(); onSelectEvent(event); }}
                    sx={{ py: 1.5, gap: 1.5 }}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {isBusy
                            ? (event.resource?.title || 'Saját esemény')
                            : `${event.resource?.eventType} – ${event.resource?.client?.firstName} ${event.resource?.client?.lastName}`}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={0.75} alignItems="center" mt={0.25}>
                          {timeStr && (
                            <Typography variant="caption" color="text.secondary">{timeStr}</Typography>
                          )}
                          <Chip
                            label={statusText}
                            color={statusColor}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                          />
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderTopColor: 'divider' }}>
        <Button onClick={onClose} size="small">Bezárás</Button>
        {onAddEvent && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutline />}
            onClick={() => { onClose(); onAddEvent(date); }}
          >
            Új esemény
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── AddEditEventDialog ──────────────────────────────────────────────────────

const AddEditEventDialog = ({ open, onClose, form, onChange, onSave, saving, isEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={isEdit ? <EditIcon /> : <AddCircleOutline />}
            title={isEdit ? 'Esemény szerkesztése' : 'Új esemény'}
            onClose={onClose}
          />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 2.5 }}>
            <TextField
              label="Esemény neve (opcionális)"
              value={form.title}
              onChange={(e) => onChange('title', e.target.value)}
              fullWidth
              size="small"
              placeholder="pl. Esküvő, Fotózás..."
            />
            <DatePicker
              label="Dátum"
              value={form.date}
              onChange={(val) => onChange('date', val)}
              minDate={isEdit ? undefined : new Date()}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.allDay}
                  onChange={(e) => onChange('allDay', e.target.checked)}
                />
              }
              label="Egész napos"
            />
            {!form.allDay && (
              <>
                <TimePicker
                  label="Kezdési időpont"
                  value={form.startTime}
                  onChange={(val) => onChange('startTime', val)}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Időtartam</InputLabel>
                  <Select
                    value={form.durationHours}
                    label="Időtartam"
                    onChange={(e) => onChange('durationHours', e.target.value)}
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={onClose}>Mégse</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={onSave}
            disabled={!form.date || saving || (!form.allDay && !form.startTime)}
          >
            {saving ? 'Mentés...' : (isEdit ? 'Módosítás mentése' : 'Mentés')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

// ─── ProviderCalendar ────────────────────────────────────────────────────────

const ProviderCalendar = ({ isMini = false }) => {
  const { user, isProvider } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [currentView, setCurrentView] = useState(isMini ? 'month' : (isMobile ? 'agenda' : 'month'));
  const [currentDate, setCurrentDate] = useState(new Date());

  const [busyDates, setBusyDates] = useState([]);
  const providerId = user?.provider?.id ?? null;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [eventForm, setEventForm] = useState({
    date: null, title: '', allDay: true, startTime: null, durationHours: 2
  });
  const [saving, setSaving] = useState(false);

  const [helpOpen, setHelpOpen] = useState(false);
  const [dayEventsOpen, setDayEventsOpen] = useState(false);
  const [dayEventsDate, setDayEventsDate] = useState(null);

  const fetchBookings = useCallback(async () => {
    if (!user || !isProvider()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await bookingService.getBookings({ status: 'ALL', limit: 500 });
      if (response.success) {
        const calendarEvents = response.data.map(booking => {
          const eventStart = new Date(booking.eventDate);
          const [hours, minutes] = booking.eventTime.split(':').map(Number);
          eventStart.setHours(hours, minutes, 0, 0);
          const eventEnd = addHours(eventStart, booking.durationHours || 1);
          return {
            id: `booking-${booking.id}`,
            title: `${booking.eventType} - ${booking.client?.firstName} ${booking.client?.lastName}`,
            start: eventStart,
            end: eventEnd,
            allDay: false,
            resource: booking,
            type: 'BOOKING'
          };
        });
        setEvents(calendarEvents);
      } else {
        setError(response.message || 'Foglalások betöltése sikertelen.');
      }
    } catch (err) {
      console.error('Naptár foglalások betöltése hiba:', err);
      setError('Hiba történt a foglalások betöltése során.');
    } finally {
      setLoading(false);
    }
  }, [user, isProvider]);

  const fetchBusyDates = useCallback(async () => {
    if (!providerId) return;
    try {
      const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROVIDER_CALENDAR.BUSY_DATES(providerId)}`);
      const data = await res.json();
      if (data.success) setBusyDates(data.data);
    } catch (err) {
      console.error('Foglalt napok betöltése hiba:', err);
    }
  }, [providerId]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);
  useEffect(() => { fetchBusyDates(); }, [fetchBusyDates]);

  const allEvents = useMemo(() => [
    ...events,
    ...busyDates
      .filter(bd => bd.source !== 'BOOKING')
      .map(bd => {
        const d = new Date(bd.date);
        const isTimedEvent = !!bd.startTime;
        let start, end;

        if (isTimedEvent) {
          const [h, m] = bd.startTime.split(':').map(Number);
          start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m);
          end = addHours(start, bd.durationHours || 1);
        } else {
          start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
          end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
        }

        return {
          id: `busy-${bd.id}`,
          title: bd.source === 'MANUAL' ? `🔒 ${bd.title || 'Saját esemény'}` : `📅 ${bd.title || 'Importált esemény'}`,
          start,
          end,
          allDay: !isTimedEvent,
          resource: bd,
          type: 'BUSY'
        };
      })
  ], [events, busyDates]);

  // A naptárnak ténylegesen átadott eseménylista: Hét/Nap nézetben az "eltolt
  // nap" trükkhöz a nem egész-napos események idejét eltoljuk (lásd fentebb),
  // Hónap/Lista nézetben (és az egész napos eseményeknél, amik külön sávban,
  // a rácstól függetlenül jelennek meg) a valós időt hagyjuk változatlanul.
  const displayEvents = useMemo(() => {
    if (!isTimeGridView(currentView)) return allEvents;
    return allEvents.map(ev => {
      if (ev.allDay) return ev;
      return { ...ev, start: shiftForDisplay(ev.start), end: shiftForDisplay(ev.end) };
    });
  }, [allEvents, currentView]);

  const eventsOnDate = useCallback((date) => {
    return allEvents.filter(ev =>
      new Date(ev.start).toDateString() === new Date(date).toDateString()
    );
  }, [allEvents]);

  const DateHeaderComponent = useCallback(({ date, label, onDrillDown }) => {
    const count = eventsOnDate(date).length;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%', pr: 0.5 }}>
        {count > 0 && (
          <Box component="span" sx={{
            display: 'inline-block',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '8px',
            px: '5px',
            fontSize: '0.6rem',
            fontWeight: 700,
            lineHeight: '16px',
            minWidth: '16px',
            textAlign: 'center',
          }}>
            {count}
          </Box>
        )}
        <button onClick={onDrillDown} className="rbc-button-link">{label}</button>
      </Box>
    );
  }, [eventsOnDate]);

  const EventComponent = ({ event }) => (
    <Box sx={{ p: isMini ? 0 : 0.5, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      {!isMini && <EventIcon sx={{ fontSize: 14, mr: 0.5 }} />}
      <Typography variant={isMini ? "caption" : "body2"} noWrap sx={{ flexGrow: 1 }}>
        {event.title}
      </Typography>
    </Box>
  );

  // Vízszintes elválasztó csík a valós éjfélnek megfelelő sávnál (Hét/Nap
  // nézet, "eltolt nap" rács) — jelzi, hogy onnantól már a következő naptári
  // nap kezdődik, bár vizuálisan még ugyanabban az oszlopban folytatódik.
  // (Korábban minden oszlopban egy "Következő nap" felirat is megjelent itt,
  // de az minden napra külön-külön ismételve inkább zavaró "kis dobozoknak"
  // hatott, mint hasznos jelzésnek — maga a vonal önmagában is egyértelmű.)
  const MidnightSlotWrapper = useCallback(({ value, children }) => {
    const isMidnight = value && unshiftFromDisplay(value).getHours() === 0 && unshiftFromDisplay(value).getMinutes() === 0;
    if (!isMidnight) return children;
    return (
      <Box sx={{
        position: 'relative', width: '100%', height: '100%',
        borderTop: `2px dashed ${theme.palette.secondary.main}`,
      }}>
        {children}
      </Box>
    );
  }, [theme]);

  const calendarComponents = useMemo(() => ({
    event: EventComponent,
    month: { dateHeader: DateHeaderComponent },
    ...(isTimeGridView(currentView) ? { timeSlotWrapper: MidnightSlotWrapper } : {}),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [DateHeaderComponent, MidnightSlotWrapper, currentView]);

  // Hét/Nap nézetben a rács-sorok és az esemény-időfeliratok a "valós" órát
  // mutassák, ne az eltolt (megjelenítési) időt — lásd a DAY_START_HOUR trükk
  // magyarázatát a fájl elején.
  const calendarFormats = useMemo(() => {
    if (!isTimeGridView(currentView)) return undefined;
    const fmt = (date) => format(unshiftFromDisplay(date), 'HH:mm', { locale: hu });
    return {
      timeGutterFormat: (date) => fmt(date),
      eventTimeRangeFormat: ({ start, end }) => `${fmt(start)} – ${fmt(end)}`,
      eventTimeRangeStartFormat: ({ start }) => `${fmt(start)} –`,
      eventTimeRangeEndFormat: ({ end }) => `– ${fmt(end)}`,
    };
  }, [currentView]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setEventDetailOpen(true);
  };

  const handleViewChange = (newView) => {
    if (!isMini) setCurrentView(newView);
  };

  const handleFormChange = (field, value) => {
    setEventForm(prev => ({ ...prev, [field]: value }));
  };

  const openAddEventDialog = (prefilledDate = null, prefilledTime = null, allDay = true) => {
    setIsEditMode(false);
    setEventForm({ date: prefilledDate, title: '', allDay, startTime: prefilledTime, durationHours: 2 });
    setEventFormOpen(true);
  };

  const openEditEventDialog = (busyDate) => {
    setIsEditMode(true);
    const d = new Date(busyDate.date);
    setEventForm({
      date: d,
      title: busyDate.title || '',
      allDay: !busyDate.startTime,
      startTime: busyDate.startTime ? applyTimeStr(d, busyDate.startTime) : null,
      durationHours: busyDate.durationHours || 2,
    });
    setEventFormOpen(true);
  };

  // ─── API helpers ────────────────────────────────────────────────────────────

  const apiAddBusyDate = useCallback(async ({ dateStr, title, startTimeStr, durationHours }) => {
    const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROVIDER_CALENDAR.ADD_BUSY_DATE(providerId)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        date: dateStr,
        title: title || null,
        startTime: startTimeStr || null,
        durationHours: durationHours || null,
      }),
    });
    return res.json();
  }, [providerId]);

  const apiRemoveBusyDate = useCallback(async (busyDateId) => {
    const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROVIDER_CALENDAR.REMOVE_BUSY_DATE(providerId, busyDateId)}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    });
    return res.json();
  }, [providerId]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleSaveBusyDate = async () => {
    if (!eventForm.date || !providerId) return;
    setSaving(true);
    try {
      const dateStr = toDateStr(eventForm.date);
      const startTimeStr = !eventForm.allDay && eventForm.startTime ? format(eventForm.startTime, 'HH:mm') : null;
      const data = await apiAddBusyDate({
        dateStr,
        title: eventForm.title,
        startTimeStr,
        durationHours: !eventForm.allDay ? eventForm.durationHours : null,
      });
      if (data.success) {
        setSnackbar({ open: true, message: 'Esemény sikeresen mentve.', severity: 'success' });
        setEventFormOpen(false);
        fetchBusyDates();
      } else {
        setSnackbar({ open: true, message: data.message || 'Hiba a mentés során.', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Hálózati hiba.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBusyDate = async () => {
    if (!selectedEvent?.resource || !providerId || !eventForm.date) return;
    setSaving(true);
    try {
      const busyDateId = selectedEvent.resource.id;
      const oldDateStr = toUtcDateStr(new Date(selectedEvent.resource.date));
      const newDateStr = toDateStr(eventForm.date);
      const startTimeStr = !eventForm.allDay && eventForm.startTime ? format(eventForm.startTime, 'HH:mm') : null;
      const durationHours = !eventForm.allDay ? eventForm.durationHours : null;

      const dateChanged = oldDateStr !== newDateStr;

      let success = false;

      if (dateChanged) {
        // Dátum változott: régi törlése + új létrehozása
        const delData = await apiRemoveBusyDate(busyDateId);
        if (delData.success) {
          const addData = await apiAddBusyDate({
            dateStr: newDateStr,
            title: eventForm.title,
            startTimeStr,
            durationHours,
          });
          success = addData.success;
          if (!addData.success) {
            setSnackbar({ open: true, message: addData.message || 'Hiba a mentés során.', severity: 'error' });
          }
        } else {
          setSnackbar({ open: true, message: delData.message || 'Hiba a törlés során.', severity: 'error' });
        }
      } else {
        // Csak egyéb mező változott: PATCH
        const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROVIDER_CALENDAR.UPDATE_BUSY_DATE(providerId, busyDateId)}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ title: eventForm.title || null, startTime: startTimeStr, durationHours }),
        });
        const data = await res.json();
        success = data.success;
        if (!data.success) {
          setSnackbar({ open: true, message: data.message || 'Hiba a módosítás során.', severity: 'error' });
        }
      }

      if (success) {
        setSnackbar({ open: true, message: 'Esemény sikeresen módosítva.', severity: 'success' });
        setEventFormOpen(false);
        setEventDetailOpen(false);
        fetchBusyDates();
      }
    } catch {
      setSnackbar({ open: true, message: 'Hálózati hiba.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // DnD esemény áthelyezése: törlés régi dátumról + létrehozás az újra
  const handleEventDrop = useCallback(async ({ event, start }) => {
    if (event.type === 'BOOKING') {
      setSnackbar({ open: true, message: 'A foglalások dátuma nem módosítható innen.', severity: 'warning' });
      return;
    }
    if (event.type !== 'BUSY' || event.resource?.source === 'BOOKING') return;

    // Hét/Nap nézetben a naptár az "eltolt nap" rácsból adja vissza a start-ot
    // (lásd DAY_START_HOUR) — mielőtt bármit számolnánk vele, visszaalakítjuk
    // valós idővé. Egész napos eseményeknél nincs eltolás, ott nincs teendő.
    if (isTimeGridView(currentView) && !event.allDay) {
      start = unshiftFromDisplay(start);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start < today) {
      setSnackbar({ open: true, message: 'Múltbeli napra nem helyezhet át eseményt.', severity: 'warning' });
      return;
    }

    const oldDateStr = toUtcDateStr(new Date(event.resource.date));
    const newDateStr = toDateStr(start);

    if (oldDateStr === newDateStr && !event.resource.startTime) return;

    try {
      const isTimedEvent = !!event.resource.startTime;
      const newStartTimeStr = isTimedEvent ? format(start, 'HH:mm') : null;

      const delData = await apiRemoveBusyDate(event.resource.id);
      const addData = delData.success
        ? await apiAddBusyDate({
            dateStr: newDateStr,
            title: event.resource.title,
            startTimeStr: newStartTimeStr,
            durationHours: event.resource.durationHours,
          })
        : { success: false };

      if (delData.success && addData.success) {
        setSnackbar({ open: true, message: 'Esemény áthelyezve.', severity: 'success' });
        fetchBusyDates();
      } else {
        setSnackbar({ open: true, message: 'Hiba az áthelyezés során.', severity: 'error' });
        fetchBusyDates(); // állapot visszaállítása
      }
    } catch {
      setSnackbar({ open: true, message: 'Hálózati hiba.', severity: 'error' });
      fetchBusyDates();
    }
  }, [apiAddBusyDate, apiRemoveBusyDate, fetchBusyDates, currentView]);

  const handleIcsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !providerId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('ics', file);
      const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROVIDER_CALENDAR.UPLOAD_ICS(providerId)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setSnackbar({ open: true, message: data.message, severity: 'success' });
        fetchBusyDates();
      } else {
        setSnackbar({ open: true, message: data.message || 'Feltöltési hiba.', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Hálózati hiba a feltöltés során.', severity: 'error' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveBusyDate = async (busyDate) => {
    if (!providerId) return;
    try {
      const data = await apiRemoveBusyDate(busyDate.id);
      if (data.success) {
        setSnackbar({ open: true, message: 'Esemény törölve.', severity: 'success' });
        setEventDetailOpen(false);
        fetchBusyDates();
      } else {
        setSnackbar({ open: true, message: data.message || 'Törlési hiba.', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Hálózati hiba.', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: isMini ? 200 : 400 }}>
        <CircularProgress size={isMini ? 20 : 40} />
        <Typography sx={{ ml: 2 }} variant={isMini ? "caption" : "body1"}>Naptár betöltése...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ height: isMini ? 200 : 'auto' }}>{error}</Alert>;
  }

  return (
    <Box>
      {!isMini && (
        // Telefonon is egy sorba tördelődő, kompakt eszközsor (korábban 3 teljes szélességű gomb egymás alatt)
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }} flexWrap="wrap" useFlexGap>
          <input
            type="file"
            accept=".ics"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleIcsUpload}
          />
          <Tooltip title="Töltse fel Google/Apple/Outlook naptárából exportált .ics fájlját">
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !providerId}
              size="small"
            >
              {uploading ? 'Feltöltés...' : (isMobile ? 'ICS import' : 'ICS naptár importálása')}
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Block />}
            onClick={() => openAddEventDialog(null, null, true)}
            disabled={!providerId}
            size="small"
            color="warning"
          >
            Nap zárolása
          </Button>
          {isMobile ? (
            <Tooltip title="Hogyan exportálja a naptárát?">
              <IconButton size="small" aria-label="Hogyan exportálja a naptárát?" onClick={() => setHelpOpen(true)} sx={{ width: 36, height: 36, color: 'primary.main' }}>
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="text"
              startIcon={<HelpOutline />}
              onClick={() => setHelpOpen(true)}
              size="small"
            >
              Hogyan exportáljam?
            </Button>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: theme.palette.success.main }} />
            <Typography variant="caption">Foglalás</Typography>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: theme.palette.grey[500] }} />
            <Typography variant="caption">Saját esemény</Typography>
          </Stack>
        </Stack>
      )}

      <Box sx={{
        height: isMini ? 300 : (isMobile ? 450 : 580),
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        // Naptár cellaháttér: fehér/közel fehér, hogy világosabb legyen a konténernél
        '& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view': {
          backgroundColor: '#fff',
        },
        '& .rbc-day-bg': {
          backgroundColor: '#fff',
        },
        '& .rbc-off-range-bg': {
          backgroundColor: theme.palette.secondary.dark, // #f5f1e6 – hónap határain kívüli napok
        },
        '& .rbc-today': {
          backgroundColor: `${theme.palette.primary.main}12`, // primary 7% opacitás
        },
        '& .rbc-header': {
          fontSize: isMini ? '0.7rem' : '0.9rem',
          backgroundColor: `${theme.palette.primary.main}10`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 0.5,
        },
        '& .rbc-time-header-content': {
          backgroundColor: `${theme.palette.primary.main}10`,
        },
        '& .rbc-time-content': {
          backgroundColor: '#fff',
        },
        '& .rbc-agenda-view table': {
          backgroundColor: '#fff',
        },
        // Toolbar háttér
        '& .rbc-toolbar': {
          display: isMini ? 'none' : 'flex',
          backgroundColor: `${theme.palette.primary.main}08`,
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          // Telefonon kisebb, egy sorba férő gombok
          ...(isMobile ? {
            gap: 0.75,
            '& .rbc-toolbar-label': { order: -1, width: '100%', fontWeight: 600, py: 0.25 },
            '& .rbc-btn-group button': { padding: '4px 8px', fontSize: '0.8rem' },
          } : {}),
        },
        // Üres lista nézet: ne egy nagy üres fehér doboz legyen
        '& .rbc-agenda-empty': { p: 2, color: theme.palette.text.secondary },
        '& .rbc-event': { borderRadius: '4px' },
        '& .rbc-day-bg + .rbc-day-bg': { borderLeft: `1px solid ${theme.palette.divider}` },
        '& .rbc-month-row': { minHeight: isMini ? 'unset' : 'auto' },
        // A beépített piros "most" vonal a valós 0-24 órás skálához igazodna,
        // de a Hét/Nap nézet rácsa a DAY_START_HOUR trükk miatt el van tolva
        // — a beépített vonal ezért rossz sorban jelenne meg, elrejtjük.
        ...(isTimeGridView(currentView) ? { '& .rbc-current-time-indicator': { display: 'none' } } : {}),
      }}>
        <DnDCalendar
          localizer={localizer}
          events={displayEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          onEventDrop={handleEventDrop}
          // Átméretezés egyelőre tiltva (csak nap-áthelyezés támogatott)
          resizable={false}
          // BOOKING eseményeket nem lehet húzni
          draggableAccessor={(event) => event.type === 'BUSY' && event.resource?.source !== 'BOOKING'}
          messages={{
            next: 'Következő',
            previous: 'Előző',
            today: 'Ma',
            month: 'Hónap',
            week: 'Hét',
            day: 'Nap',
            agenda: 'Lista',
            date: 'Dátum',
            time: 'Idő',
            event: 'Esemény',
            noEventsInRange: 'Nincs esemény ebben az időszakban.',
          }}
          culture="hu"
          components={calendarComponents}
          formats={calendarFormats}
          views={isMini ? ['month'] : ['month', 'week', 'day', 'agenda']}
          date={currentDate}
          onNavigate={(d) => setCurrentDate(d)}
          view={currentView}
          onView={handleViewChange}
          defaultView={isMini ? 'month' : (isMobile ? 'agenda' : 'month')}
          // Hét/Nap nézetben a teljes (eltolt-térbeli) 24 órát mutatjuk — ez a
          // valós idő szerint 7:00-tól a következő nap 7:00-jáig tart (lásd
          // DAY_START_HOUR). Hónap/Lista nézetben ezt a propot a könyvtár
          // figyelmen kívül hagyja.
          min={startOfDay(new Date(0, 0, 0, 0, 0, 0))}
          max={endOfDay(new Date(0, 0, 0, 0, 0, 0))}
          selectable={!isMini}
          onShowMore={(_, date) => {
            setDayEventsDate(date);
            setDayEventsOpen(true);
          }}
          onSelectSlot={({ start, action }) => {
            // Csak kattintásra reagálunk – a tartomány-húzást (select) kizárjuk,
            // hogy ne nyíljon dialog amikor valaki eseményt próbál áthúzni
            if (isMini || action !== 'click') return;
            const isMonthView = currentView === 'month';
            // Hét/Nap nézetben a kapott slot az "eltolt nap" rácsból jön —
            // visszaalakítjuk valós idővé, mielőtt bármit kezdenénk vele.
            if (isTimeGridView(currentView)) start = unshiftFromDisplay(start);
            if (isMonthView && eventsOnDate(start).length > 0) {
              setDayEventsDate(start);
              setDayEventsOpen(true);
              return;
            }
            const hasTime = !isMonthView && start.getHours() !== 0;
            openAddEventDialog(start, hasTime ? start : null, isMonthView || !hasTime);
          }}
          eventPropGetter={(event) => {
            if (event.type === 'BUSY') {
              return { style: { backgroundColor: theme.palette.grey[500], color: '#fff', opacity: 0.85 } };
            }
            const backgroundColor = theme.palette[getStatusColor(event.resource?.status)]?.main || theme.palette.grey[500];
            const color = theme.palette[getStatusColor(event.resource?.status)]?.contrastText || '#fff';
            return { style: { backgroundColor, color } };
          }}
        />
      </Box>

      {/* ─── Esemény részletek dialog ─────────────────────────────────────── */}
      <Dialog open={eventDetailOpen} onClose={() => setEventDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<EventIcon />}
            title="Esemény részletei"
            onClose={() => setEventDetailOpen(false)}
          />
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ pt: 4, px: 3, pb: 1 }}>
          {selectedEvent?.type === 'BUSY' && selectedEvent.resource && (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  {selectedEvent.resource.title || 'Saját esemény'}
                </Typography>
                <Chip
                  label={
                    selectedEvent.resource.source === 'MANUAL' ? 'Személyes esemény' :
                    selectedEvent.resource.source === 'ICS_IMPORT' ? 'Importált esemény' : 'Foglalásból'
                  }
                  color={selectedEvent.resource.source === 'BOOKING' ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Dátum</Typography>
                <Typography variant="body1">
                  {format(new Date(selectedEvent.resource.date), 'yyyy. MM. dd.', { locale: hu })}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Időpont</Typography>
                <Typography variant="body1">
                  {selectedEvent.resource.startTime
                    ? `${selectedEvent.resource.startTime}${selectedEvent.resource.durationHours ? ` (${selectedEvent.resource.durationHours} óra)` : ''}`
                    : 'Egész napos'}
                </Typography>
              </Grid>
              {selectedEvent.resource.source !== 'BOOKING' && (
                <Grid size={12}>
                  <Button
                    color="error"
                    startIcon={<DeleteOutline />}
                    onClick={() => handleRemoveBusyDate(selectedEvent.resource)}
                    size="small"
                  >
                    Törlés
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
          {selectedEvent?.type === 'BOOKING' && selectedEvent.resource && (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>{selectedEvent.resource.eventType}</Typography>
                <Chip
                  label={getStatusText(selectedEvent.resource.status)}
                  color={getStatusColor(selectedEvent.resource.status)}
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid size={12}><Divider sx={{ my: 1 }} /></Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Dátum</Typography>
                <Typography variant="body1">{format(new Date(selectedEvent.resource.eventDate), 'yyyy. MM. dd.', { locale: hu })}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Időpont</Typography>
                <Typography variant="body1">{selectedEvent.resource.eventTime}</Typography>
              </Grid>
              {selectedEvent.resource.durationHours && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Időtartam</Typography>
                  <Typography variant="body1">{selectedEvent.resource.durationHours} óra</Typography>
                </Grid>
              )}
              {selectedEvent.resource.guestCount && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Vendégek</Typography>
                  <Typography variant="body1">{selectedEvent.resource.guestCount} fő</Typography>
                </Grid>
              )}
              <Grid size={12}><Divider sx={{ my: 1 }} /></Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">Ügyfél</Typography>
                <Typography variant="body1">{selectedEvent.resource.client?.firstName} {selectedEvent.resource.client?.lastName}</Typography>
                <Typography variant="body2"><MessageIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /> {selectedEvent.resource.contactEmail}</Typography>
                {selectedEvent.resource.contactPhone && (
                  <Typography variant="body2"><Phone sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /> {selectedEvent.resource.contactPhone}</Typography>
                )}
              </Grid>
              {selectedEvent.resource.offerPrice && (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Ajánlott ár</Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">{parseFloat(selectedEvent.resource.offerPrice).toLocaleString()} Ft</Typography>
                </Grid>
              )}
            </Grid>
          )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setEventDetailOpen(false)}>Bezárás</Button>
          {selectedEvent?.type === 'BUSY' && selectedEvent.resource?.source !== 'BOOKING' && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                openEditEventDialog(selectedEvent.resource);
                setEventDetailOpen(false);
              }}
            >
              Szerkesztés
            </Button>
          )}
          {selectedEvent?.type === 'BOOKING' && (
            <Button variant="contained" onClick={() => {
              setEventDetailOpen(false);
              navigate(`/foglalasok?bookingId=${selectedEvent.resource.id}`);
            }}>
              Foglalás kezelése
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ─── Nap-összesítő dialog ─────────────────────────────────────────── */}
      <DayEventsDialog
        open={dayEventsOpen}
        onClose={() => setDayEventsOpen(false)}
        date={dayEventsDate}
        events={dayEventsDate ? eventsOnDate(dayEventsDate) : []}
        onSelectEvent={handleSelectEvent}
        onAddEvent={(date) => openAddEventDialog(date, null, true)}
      />

      {/* ─── Esemény létrehozás / szerkesztés dialog ─────────────────────── */}
      <AddEditEventDialog
        open={eventFormOpen}
        onClose={() => setEventFormOpen(false)}
        form={eventForm}
        onChange={handleFormChange}
        onSave={isEditMode ? handleUpdateBusyDate : handleSaveBusyDate}
        saving={saving}
        isEdit={isEditMode}
      />

      {/* ─── ICS súgó dialog ─────────────────────────────────────────────── */}
      <ProviderCalendarHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* ─── Snackbar ────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ProviderCalendar;
